import { useEffect, useState } from 'react'
import cx from 'classnames'
import dayjs from 'dayjs'
import Styles from './V4StrategySelection.module.scss'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { Text } from 'blaze-ui/Text/Text'
import { Check2 } from 'blaze-ui/icons/20'
import { TopBar } from '../TopBar/TopBar'
import { V4StepFooter } from '../V4StepFooter'
import { useStepAction } from '../useStepAction'
import {
  strategiesDefaultDataV2,
  strategiesToContentStrategies,
  Strategy,
  buildContentStrategyTactics,
  mergeStrategyDefaults,
  whyWeRecommendIt,
  description,
} from '../StrategiesSelection/utils'
import { ContentStrategyTypeEmoji } from 'components/ContentStrategyTypeEmoji/ContentStrategyTypeEmoji'
import { ImgMap, SmallThumbMap } from 'components/ContentStrategyTypeImg/ContentStrategyTypeImg'
import { useUpdateBrandPlan } from 'hooks/queries/useUpdateBrandPlan'
import { useBrandPlan } from 'hooks/queries/brandPlans/useBrandPlan'
import { useContentDefaults } from 'hooks/queries/useContentDefaults'
import { BrandPlanTactic, BusinessType, Goal } from 'types'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { useRootStore } from 'stores/RootStore'
import { useCreditsToGenerateContent } from 'hooks/useNeedsCreditsToGenerateContent'
import { observer } from 'mobx-react-lite'

interface Props {
  onNext: (brandPlanStrategies: Strategy[], planKey: PaymentPlanKey) => void
  onBack: () => void
  initialSelectedStrategies?: Strategy[]
  selectedBusinessType: BusinessType
  goal: Goal
  brandPlanId: number
  isSaving?: boolean
  highlightStep?: string
}

export const V4StrategySelection = observer(
  ({
    onNext,
    onBack,
    initialSelectedStrategies,
    selectedBusinessType,
    goal,
    brandPlanId,
    isSaving,
    highlightStep = 'strategy',
  }: Props) => {
    const { isSaving: isUpdatingBrandPlan, run } = useStepAction()
    const { data: brandPlan } = useBrandPlan(brandPlanId)
    const { data: contentDefaults } = useContentDefaults()
    const {
      tierUsageStore: { autopilotPlans },
    } = useRootStore()

    const starterPlanCreditLimit = Number(
      autopilotPlans.find((plan) => plan.key === PaymentPlanKey.BlazeAutopilotStarter)?.prices[0]?.metadata
        ?.credit_limit ?? 0,
    )

    const mergedDefaults = mergeStrategyDefaults(strategiesDefaultDataV2, contentDefaults?.content_strategy_defaults)

    const [strategies, setStrategies] = useState<Strategy[]>(() => {
      if (initialSelectedStrategies?.length) return initialSelectedStrategies
      return mergedDefaults[selectedBusinessType].strategies.map((s, i) => ({ ...s, isSelected: i === 0 }))
    })

    useEffect(() => {
      if (initialSelectedStrategies?.some((s) => s.isSelected)) return
      setStrategies(mergedDefaults[selectedBusinessType].strategies.map((s, i) => ({ ...s, isSelected: i === 0 })))
    }, [initialSelectedStrategies, contentDefaults]) // eslint-disable-line react-hooks/exhaustive-deps

    const selectedStrategy = strategies.find((s) => s.isSelected) ?? strategies[0]

    const handleSelect = (strategyId: string) => {
      setStrategies(strategies.map((s) => ({ ...s, isSelected: s.id === strategyId })))
    }

    const { mutateAsync: updateBrandPlan } = useUpdateBrandPlan({ id: brandPlanId })

    const handleNext = () =>
      run(async () => {
        if (!selectedStrategy) return

        const selectedBlogPlatform = brandPlan?.selected_blog_platform ?? ExternalIntegrationEnum.WordPress
        const selectedEmailPlatform = brandPlan?.selected_email_platform ?? ExternalIntegrationEnum.Mailchimp

        const selectedStrategiesValues = [selectedStrategy].map((strategy) => ({
          ...strategy,
          tactics: buildContentStrategyTactics(strategy),
        }))

        const s = selectedStrategiesValues[0]

        await updateBrandPlan({
          content_strategies: strategiesToContentStrategies(selectedStrategiesValues),
          tactics: s.tactics,
          crosspost_contents: s.crosspost_contents,
          video_crosspost_contents: s.video_crosspost_contents,
          seo_contents: s.seo_contents,
          email_contents: s.email_contents,
          instagram_contents: s.instagram_contents,
          goal,
          business_type: selectedBusinessType,
          start_date: dayjs().add(1, 'day').format('YYYY-MM-DD'),
          post_content_types: true,
          selected_blog_platform: s.tactics.includes(BrandPlanTactic.SEO) ? selectedBlogPlatform : null,
          selected_email_platform: s.tactics.includes(BrandPlanTactic.Email) ? selectedEmailPlatform : null,
        })

        onNext(strategies, isGrowthPlan ? PaymentPlanKey.BlazeAutopilotGrowth : PaymentPlanKey.BlazeAutopilotStarter)
      }, 'An error occurred during content strategy selection. Try again. Contact support if the issue persists.')

    const totalBlogCount = selectedStrategy?.seo_contents.reduce((sum, c) => sum + c.count, 0) ?? 0
    const totalEmailCount = selectedStrategy?.email_contents.reduce((sum, c) => sum + c.count, 0) ?? 0
    const totalShortFormVideoCount =
      selectedStrategy?.video_crosspost_contents.reduce((sum, c) => sum + c.count, 0) ?? 0

    const crosspostContents = selectedStrategy?.crosspost_contents ?? []
    const stillImageCount = crosspostContents
      .filter((c) => c.content_type === 'still_image')
      .reduce((sum, c) => sum + c.count, 0)
    const carouselCount = crosspostContents
      .filter((c) => c.content_type === 'carousel')
      .reduce((sum, c) => sum + c.count, 0)
    const feedVideoCount = crosspostContents
      .filter((c) => c.content_type === 'video')
      .reduce((sum, c) => sum + c.count, 0)
    const totalSocialCount = 0

    const credits = useCreditsToGenerateContent(
      totalSocialCount,
      totalBlogCount,
      totalEmailCount,
      totalShortFormVideoCount,
      stillImageCount,
      feedVideoCount,
      carouselCount,
      0,
      { applyVisualStyle: true, percentageOfVisualStylesGeneration: 1 },
    )
    const monthlyCredits = Math.floor(credits.creditsNeeded * 4.33)
    const isGrowthPlan = monthlyCredits >= starterPlanCreditLimit

    const isSavingChanges = isUpdatingBrandPlan || isSaving
    const defaultStrategyId = strategies[0]?.id

    return (
      <MultiStepStepLayout scrollContainerClassName={Styles.scrollContainer}>
        <V4StepFooter
          onBack={onBack}
          onNext={handleNext}
          isSaving={isSavingChanges}
          isDisabled={!selectedStrategy}
          nextLabel={`Continue with ${selectedStrategy?.name ?? 'this strategy'}`}
          savingLabel="Saving changes..."
        >
          <TopBar highlightStep={highlightStep as React.ComponentProps<typeof TopBar>['highlightStep']} />

          <div className={Styles.content}>
            <div className={Styles.headerSection}>
              <p className={Styles.heading}>Pick your content strategy</p>
              <p className={Styles.subtitle}>
                Your strategy sets the direction for every piece of content Blaze makes.
              </p>
            </div>

            <div className={Styles.cardList}>
              {strategies.map((strategy, i) => {
                const isSelected = !!strategy.isSelected

                return (
                  <div key={strategy.id} className={Styles.cardItem}>
                    {i === 1 && (
                      <p className={Styles.otherLabel}>
                        Other strategies which businesses in your industry do and you can start with as well.
                      </p>
                    )}

                    {/* Expanded (selected) state */}
                    <div className={cx(Styles.expandedWrap, { [Styles.expandedVisible]: isSelected })}>
                      <div>
                        <div className={Styles.largeCard}>
                          <div className={cx(Styles.largeCardGradient, Styles[strategy.id])}>
                            {strategy.id === defaultStrategyId && (
                              <div className={Styles.recommendedPill}>Recommended</div>
                            )}
                            <div className={Styles.checkIndicator}>
                              <Check2 size={15} color="var(--light-100)" />
                            </div>
                            <div className={Styles.largeCardText}>
                              <p className={Styles.largeCardTitle}>
                                <ContentStrategyTypeEmoji contentStrategyType={strategy.id} /> {strategy.name}
                              </p>
                              <p className={Styles.largeCardDescription}>{description[strategy.id]}</p>
                            </div>
                            {ImgMap[strategy.id] && (
                              <img src={ImgMap[strategy.id]} alt={strategy.name} className={Styles.largeCardImg} />
                            )}
                            <div className={Styles.whyBar}>
                              <Text variant="secondary" color="rgba(255,255,255,0.6)">
                                Why we recommend it:{' '}
                                <span className={Styles.whyBarHighlight}>{whyWeRecommendIt[strategy.id]}</span>
                              </Text>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Collapsed (unselected) state */}
                    <div className={cx(Styles.collapsedWrap, { [Styles.collapsedVisible]: !isSelected })}>
                      <div>
                        <button type="button" className={Styles.smallCard} onClick={() => handleSelect(strategy.id)}>
                          <div className={Styles.smallCardThumbWrap}>
                            <div className={cx(Styles.smallCardThumb, Styles[strategy.id])}>
                              {SmallThumbMap[strategy.id] && (
                                <img
                                  src={SmallThumbMap[strategy.id]}
                                  alt={strategy.name}
                                  className={Styles.smallCardThumbImg}
                                />
                              )}
                            </div>
                          </div>
                          <div className={Styles.smallCardBody}>
                            <p className={Styles.smallCardTitle}>
                              <ContentStrategyTypeEmoji contentStrategyType={strategy.id} /> {strategy.name}
                            </p>
                            <p className={Styles.smallCardDescription}>{description[strategy.id]}</p>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </V4StepFooter>
      </MultiStepStepLayout>
    )
  },
)
